import { Trash } from '@phosphor-icons/react';
import { countChars, type ContentFormat, type DraftFlag, type TextLimits } from '@app/shared';
import { useT } from '../i18n';
import { cn } from '../lib/cn';
import { roleAt, textLimit, titleLimit, type EditorBlock } from '../lib/draft-editor';
import { AutoTextarea, Button } from './ui';

function Counter({ n, max }: { n: number; max: number }) {
  const t = useT();
  return (
    <span className={cn('text-xs tabular-nums flex-shrink-0', n > max ? 'text-destructive font-semibold' : 'text-hint')}>
      {t.draft.counter(n, max)}
    </span>
  );
}

export interface DraftBlockEditorProps {
  block: EditorBlock;
  index: number;
  total: number;
  label: string;
  format: ContentFormat;
  limits: TextLimits;
  /** An AI edit runs: the fields wait (spec 4.4). */
  readOnly: boolean;
  /** Open notes of the check that sit under this field. */
  flags: DraftFlag[];
  /** The action in flight, e.g. `remove:f1`: its button spins, the others wait. */
  acting: string | null;
  /** A build refused at this block (?block=b2): the field is outlined. */
  highlighted?: boolean;
  onText: (value: string) => void;
  onTitle: (value: string) => void;
  onBlur: () => void;
  onRemove: () => void;
  onInsertAfter: () => void;
  onFlag: (flagId: string, action: 'keep' | 'remove') => void;
}

/**
 * One block of «Формат и текст», always editable (spec 4.4): the title (carousel
 * only), the text with live counters, «Удалить блок», «+ пункт», and the notes of
 * the check under it with «Оставить» / «Убрать».
 */
export function DraftBlockEditor({
  block,
  index,
  total,
  label,
  format,
  limits,
  readOnly,
  flags,
  acting,
  highlighted = false,
  onText,
  onTitle,
  onBlur,
  onRemove,
  onInsertAfter,
  onFlag,
}: DraftBlockEditorProps) {
  const t = useT();
  const f = t.formatText;
  const max = textLimit(format, roleAt(index, total), limits);
  return (
    <div className={cn('rounded-xl bg-surface shadow-card p-4', highlighted && 'ring-2 ring-destructive')}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-xs font-bold uppercase tracking-[0.06em] text-hint min-w-0 truncate">{label}</span>
        <Counter n={countChars(block.text)} max={max} />
      </div>
      {format === 'carousel' && (
        <div className="mb-2">
          <AutoTextarea
            value={block.title}
            onChange={(e) => onTitle(e.target.value)}
            onBlur={onBlur}
            readOnly={readOnly}
            placeholder={t.draft.title}
            minRows={1}
            maxRows={3}
            className="font-semibold"
          />
          <div className="flex justify-end mt-1">
            <Counter n={countChars(block.title)} max={titleLimit(roleAt(index, total), limits)} />
          </div>
        </div>
      )}
      <AutoTextarea
        value={block.text}
        onChange={(e) => onText(e.target.value)}
        onBlur={onBlur}
        readOnly={readOnly}
        invalid={countChars(block.text) > max}
        minRows={2}
        maxRows={12}
        className="text-sm"
      />
      {flags.map((flag) => (
        <div key={flag.id} className="mt-3 rounded-lg bg-warning-subtle p-3">
          <p className="text-sm font-medium break-words">{f.check(flag.quote)}</p>
          <p className="text-xs text-hint mt-0.5 mb-2 break-words">{flag.reason}</p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={readOnly || acting !== null}
              loading={acting === `keep:${flag.id}`}
              onClick={() => onFlag(flag.id, 'keep')}
            >
              {f.keep}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={readOnly || acting !== null}
              loading={acting === `remove:${flag.id}`}
              onClick={() => onFlag(flag.id, 'remove')}
            >
              {f.remove}
            </Button>
          </div>
        </div>
      ))}
      {!readOnly && (
        <div className="flex flex-wrap justify-between gap-2 mt-2">
          <Button variant="ghost" size="sm" leftIcon={<Trash size={14} />} onClick={onRemove}>
            {f.removeBlock}
          </Button>
          <Button variant="ghost" size="sm" onClick={onInsertAfter}>
            {f.addItem}
          </Button>
        </div>
      )}
    </div>
  );
}
